/* eslint-disable no-nested-ternary */
import React from 'react';
import {Button, Typography} from '@mui/material';
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import {ExpandCircleDown, UnfoldMore, OpenInNew} from '@mui/icons-material';
import {Link} from 'react-router-dom';
import {ApolloClient, LazyQueryExecFunction} from '@apollo/client';
import {FormikErrors} from 'formik';
import {
  BulkAdminSortableFieldsEnum,
  SortingDirectionEnum,
} from '../../schema-types/globalTypes';
import {
  GetBulkAccounts,
  GetBulkAccountsVariables,
} from '../../schema-types/GetBulkAccounts';
import {BulkAdminValues, getBulkAccountsVariablesFromValues} from './utils';
import {UPDATES} from '../../shared/UPDATES';
import {renderCellDate} from '../../utils/renderCellDate';
import {BulkAccountFragment} from '../../schema-types/BulkAccountFragment';
import {SetFieldValueWithSourceKey} from '../../components/AutoForm/useAutoForm';

const CellLink = ({label, pathname}: {label: string; pathname: string}) => {
  return (
    <Link
      role="a"
      to={{pathname}}
      target="_blank"
      onClick={(e) => e.stopPropagation()}
      style={{
        textDecoration: 'none',
        color: 'black',
      }}
    >
      <span style={{display: 'flex', alignItems: 'center'}}>
        {label}
        <OpenInNew color="disabled" />
      </span>
    </Link>
  );
};

export const bulkAdminColumnsFactory = ({
  fetch,
  pageSize,
  loading,
  client,
  values,
  setFieldValue,
}: {
  fetch: LazyQueryExecFunction<GetBulkAccounts, GetBulkAccountsVariables>;
  pageSize: number;
  loading: boolean;
  client: ApolloClient<any>;
  values: BulkAdminValues;
  setFieldValue: SetFieldValueWithSourceKey<BulkAdminValues>;
}): GridColDef[] => {
  const {orderBy, orderDirection} = values;
  const renderClickableHeader = (
    params: GridColumnHeaderParams,
    thisOrderBy: BulkAdminSortableFieldsEnum
  ) => {
    return (
      <Button
        disabled={loading}
        onClick={async () => {
          if (loading) {
            return;
          }
          let newDirection = SortingDirectionEnum.ASCENDING;
          if (orderBy === thisOrderBy) {
            if (orderDirection === SortingDirectionEnum.ASCENDING) {
              newDirection = SortingDirectionEnum.DESCENDING;
            }
          }
          // regardless of what is done, we set to page 1
          setFieldValue(UPDATES._1204, 'cursors', []);
          setFieldValue(UPDATES._1208, 'orderBy', thisOrderBy);
          setFieldValue(UPDATES._1209, 'orderDirection', newDirection);
          setFieldValue(UPDATES._1203, 'search', '');

          fetch({
            variables: {
              ...getBulkAccountsVariablesFromValues(values),
              orderBy: thisOrderBy,
              orderDirection: newDirection,
              first: pageSize,
              after: null,
              searchTerm: null,
            },
          });
        }}
      >
        <strong style={{display: 'flex'}}>
          {params.colDef.headerName}
          {orderBy === thisOrderBy ? (
            orderDirection === SortingDirectionEnum.ASCENDING ? (
              <ExpandCircleDown
                style={{
                  transform: 'rotate(180deg)',
                }}
              />
            ) : (
              <ExpandCircleDown />
            )
          ) : (
            <UnfoldMore color="disabled" />
          )}
        </strong>
      </Button>
    );
  };

  return [
    {
      field: 'clientName',
      headerName: 'Client name',
      flex: 1.2,
      // minWidth: 120,
      editable: false,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<GridValidRowModel & BulkAccountFragment>
      ) => {
        const contactId = params.row?.contactId;
        if (!contactId) {
          return (
            <Typography>
              <>{params.row.clientName || ''}</>
            </Typography>
          );
        }
        return (
          <CellLink pathname={`/contacts/${contactId}`} label={`${params.value || ''}`} />
        );
      },
      renderHeader: (params: GridColumnHeaderParams) =>
        renderClickableHeader(params, BulkAdminSortableFieldsEnum.CLIENT_NAME),
    },
    {
      field: 'accountName',
      headerName: 'Account name',
      flex: 1.5,
      editable: false,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<GridValidRowModel & BulkAccountFragment>
      ) => {
        const accountId = params.row?.accountId;
        if (!accountId) {
          return (
            <Typography>
              <>{params.row.accountName || ''}</>
            </Typography>
          );
        }
        return (
          <CellLink pathname={`/account/${accountId}`} label={`${params.value || ''}`} />
        );
      },
      renderHeader: (params: GridColumnHeaderParams) =>
        renderClickableHeader(params, BulkAdminSortableFieldsEnum.ACCOUNT_NAME),
    },
    {
      field: 'propositionType',
      headerName: 'Proposition type',
      flex: 1.2,
      editable: false,
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) =>
        renderClickableHeader(params, BulkAdminSortableFieldsEnum.PROPOSITION_TYPE),
    },
    {
      field: 'nextReviewDate',
      headerName: 'Next annual review date',
      flex: 1.2,
      editable: false,
      sortable: false,
      renderCell: renderCellDate,
      renderHeader: (params: GridColumnHeaderParams) =>
        renderClickableHeader(params, BulkAdminSortableFieldsEnum.NEXT_REVIEW_DATE),
    },
    {
      field: 'meetingBookedDate',
      headerName: 'Meeting Booked date',
      flex: 1.2,
      editable: false,
      sortable: false,
      renderCell: renderCellDate,
    },
  ];
};
